// Section 3 — "Security Restrictions" (explains-page-contract.json
// s3_locks): every account restriction with its locked dollars, plus
// the constrained pool at start. A sell_no_tax restriction can lock
// weight 0 while the held position still reserves pool budget — those
// rows show the held value marked "(pool)".
import { Card, Table } from "react-bootstrap";
import { formatCurrency } from "../../../../lib/numbers";
import type { SoftSectionContext } from "./SoftCompletionContext";
import { num, sortedByValueDesc } from "./softCompletionLib";

export default function RestrictionsSection({
  ctx,
}: {
  ctx: SoftSectionContext;
}) {
  const { completion, accounts, resolveAccountName } = ctx;

  const restrictionRows = accounts.flatMap((account) =>
    (account.restriction_adjustment?.restrictions ?? []).map((r) => {
      const lockedWeight = num(r.locked_weight);
      const balance = num(account.balance);
      // sell_no_tax with locked weight 0: the position is sellable only
      // tax-free, so the full held value still reserves pool budget.
      const isPoolOnly = lockedWeight <= 0 && r.type === "sell_no_tax";
      const heldWeight = num(
        account.checkpoints?.holdings?.by_symbol?.[r.symbol],
      );
      return {
        accountId: account.account_id,
        symbol: r.symbol,
        type: r.type,
        lockedWeight,
        lockedDollars: isPoolOnly
          ? heldWeight * balance
          : lockedWeight * balance,
        isPoolOnly,
      };
    }),
  );

  const poolRows = sortedByValueDesc(completion.constrained_pool_initial);
  const poolTotal = poolRows.reduce((acc, [, v]) => acc + v, 0);

  if (restrictionRows.length === 0 && poolRows.length === 0) return null;

  return (
    <Card className="mb-3">
      <Card.Header>Security Restrictions</Card.Header>
      <Card.Body>
        {restrictionRows.length > 0 && (
          <Table size="sm" responsive className="mb-0">
            <caption className="text-muted small">
              Restrictions — locked $ = locked weight × balance; (pool) =
              sell_no_tax holding whose full value reserves pool budget
            </caption>
            <thead>
              <tr>
                <th>Account</th>
                <th>Symbol</th>
                <th>Restriction</th>
                <th className="text-end">Locked weight</th>
                <th className="text-end">Locked $</th>
              </tr>
            </thead>
            <tbody>
              {restrictionRows.map((r, i) => (
                <tr key={`${r.accountId}-${r.symbol}-${i}`}>
                  <td>{resolveAccountName(r.accountId)}</td>
                  <td>{r.symbol}</td>
                  <td>{r.type}</td>
                  <td className="text-end">{r.lockedWeight.toFixed(4)}</td>
                  <td className="text-end">
                    {formatCurrency(r.lockedDollars)}
                    {r.isPoolOnly && (
                      <span className="text-muted"> (pool)</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {poolRows.length > 0 && (
          <Table size="sm" responsive className="mt-3 mb-0">
            <caption className="text-muted small">
              Constrained pool at start — {formatCurrency(poolTotal)} total
            </caption>
            <thead>
              <tr>
                <th>Asset class</th>
                <th className="text-end">Locked $</th>
              </tr>
            </thead>
            <tbody>
              {poolRows.map(([ac, dollars]) => (
                <tr key={ac}>
                  <td>{ac}</td>
                  <td className="text-end">{formatCurrency(dollars)}</td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td>Total</td>
                <td className="text-end">{formatCurrency(poolTotal)}</td>
              </tr>
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
